/**
 * ProjectTitleBar - Compact title bar for project pages
 * Single row (~48px) with project name and navigation
 */

import { Button } from "@/components/ui/button";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Plus } from "lucide-react";
import { useAppStore } from "@/stores/appStore";

// Custom event for triggering create project dialog
export const CREATE_PROJECT_EVENT = "studio1:createProject";

export function ProjectTitleBar() {
  const navigate = useNavigate();
  const { currentProject, setCurrentProject } = useAppStore();

  // Keep these hooks for stable hook count (even though unused now)
  useState(true);
  useState("Studio");
  useState(false);

  const isProjectSectionCompressed = !!currentProject;

  // Current title - no animation, just show directly
  const title = isProjectSectionCompressed
    ? (currentProject?.name ?? "Production")
    : "Studio";

  const handleAllProductionsClick = () => {
    setCurrentProject(null);
    navigate("/productions");
  };

  return (
    <div
      className="flex items-center justify-between border-b flex-shrink-0"
      style={{
        height: "48px",
        backgroundColor: "var(--color-panel)",
        borderBottomColor: "var(--color-panel-border)",
        paddingLeft: "24px",
        paddingRight: "24px",
      }}
    >
      {/* Left: Project name (18px semibold) */}
      <h2
        className="text-lg font-semibold text-foreground text-left line-clamp-1"
        style={{ fontSize: "18px" }}
        title={title}
      >
        {title}
      </h2>

      {/* Right: New Project button (when on projects list) or All Projects button (when inside project) */}
      {!isProjectSectionCompressed ? (
        <Button
          variant="pink"
          type="button"
          onClick={() =>
            window.dispatchEvent(new CustomEvent(CREATE_PROJECT_EVENT))
          }
          className="inline-flex items-center justify-center font-medium"
          style={{
            height: "32px",
            padding: "0 14px",
            gap: "6px",
            borderRadius: "var(--radius-default)",
            fontSize: "13px",
            fontWeight: 500,
            whiteSpace: "nowrap",
            cursor: "pointer",
            transition: "all 0.15s ease",
          }}
        >
          <Plus className="w-4 h-4" />
          New Production
        </Button>
      ) : (
        <button
          type="button"
          onClick={handleAllProductionsClick}
          className="inline-flex items-center justify-center font-medium text-muted-foreground hover:text-foreground"
          style={{
            height: "32px",
            padding: "0 14px",
            gap: "6px",
            borderRadius: "var(--radius-default)",
            fontSize: "13px",
            fontWeight: 500,
            whiteSpace: "nowrap",
            transition: "all 0.15s ease",
            background: "rgba(var(--color-primitive-neutral-0-rgb), 0.05)",
            border:
              "1px solid rgba(var(--color-primitive-neutral-0-rgb), 0.12)",
            cursor: "pointer",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background =
              "rgba(var(--color-primitive-neutral-0-rgb), 0.1)";
            e.currentTarget.style.borderColor =
              "rgba(var(--color-primitive-neutral-0-rgb), 0.2)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background =
              "rgba(var(--color-primitive-neutral-0-rgb), 0.05)";
            e.currentTarget.style.borderColor =
              "rgba(var(--color-primitive-neutral-0-rgb), 0.12)";
          }}
          onMouseDown={(e) => {
            e.currentTarget.style.transform = "scale(0.97)";
          }}
          onMouseUp={(e) => {
            e.currentTarget.style.transform = "scale(1)";
          }}
        >
          <ArrowLeft className="w-4 h-4" />
          All Productions
        </button>
      )}
    </div>
  );
}
